import { defenceBudget, type Layout } from "@empire/core-sim";
import type { TargetRow } from "./attack.js";
import type { VillageEconomy } from "./village.js";
import type { BuildingLevels } from "./production.js";
import { RESOURCE_KINDS, emptyResources, type Resources } from "./economy.js";

/**
 * The village guide's chain of steps (playtest readiness, Sep 2026), in the
 * shared package since 29 Sep 2026: each step now pays a reward, and the
 * server must decide whether a step is really done with the SAME test the
 * client uses to show it — two copies of "is the Wall built" would disagree
 * in exactly the moment somebody claims.
 */

// ---------------------------------------------------------------------------
// The next step
// ---------------------------------------------------------------------------

export type GoalAction =
  | { readonly kind: "build"; readonly building: string }
  | { readonly kind: "train"; readonly unit: string; readonly count: number }
  | { readonly kind: "attack"; readonly difficulty: NonNullable<TargetRow["difficulty"]> }
  | { readonly kind: "defence" };

export interface GoalContext {
  readonly economy: VillageEconomy;
  readonly levels: BuildingLevels;
  /** Null until the target list has loaded: attack goals then wait rather than guess. */
  readonly targets: readonly Pick<TargetRow, "difficulty" | "breachedByMe">[] | null;
  /** The maze currently in the editor (what the server holds, once loaded). */
  readonly layout: Layout;
}

export interface Goal {
  readonly id: string;
  readonly title: string;
  readonly why: string;
  readonly action: GoalAction;
  readonly done: (ctx: GoalContext) => boolean;
  /**
   * Paid once when the step is done, of each resource (29 Sep 2026). Sized so
   * each step pays for most of the next one: Travian's first quests do the
   * same, and it is what keeps a new village moving past its first build.
   */
  readonly reward: number;
  /**
   * False for a step that can come undone (the army shrinks, the road gets
   * shorter). The server then also accepts its claim once a LATER step is
   * done — the player got here, and a battle since must not cost them it.
   */
  readonly stable: boolean;
}

const lvl = (ctx: GoalContext, id: string): number => ctx.levels[id] ?? 0;

const breached = (ctx: GoalContext, difficulty: string): boolean =>
  ctx.targets?.some((t) => t.difficulty === difficulty && t.breachedByMe) ?? false;

function militiaOnTheWay(ctx: GoalContext): number {
  const have = ctx.economy.units.find((u) => u.id === "militia")?.have ?? 0;
  const training = ctx.economy.training
    .filter((t) => t.unit === "militia")
    .reduce((n, t) => n + t.count - t.delivered, 0);
  return have + training;
}

/**
 * The building is at this level, OR the builder is already on it.
 *
 * A step is finished the moment the player DOES the thing it asks. Waiting for
 * the timer as well left the same step on screen for minutes after its button
 * had been pressed, with nothing to do but look at it (playtest, 29 Sep 2026).
 */
function reached(ctx: GoalContext, id: string, level: number): boolean {
  return lvl(ctx, id) >= level || ctx.economy.queue.some((q) => q.building === id && q.level >= level);
}

function roadUsed(layout: Layout): number {
  return layout.tiles.filter((t) => t.kind === "road" || t.kind === "mud" || t.kind === "barricade").length;
}

/**
 * The first twenty minutes, as a chain. One step on screen at a time.
 *
 * Deliberately short and deliberately in this order: a win first (the easy
 * barbarian village, D-113), then the army that win cost, then the economy
 * that feeds it, then the defence the village is for. It ends by pointing at
 * the medium barbarian village — the first fight that needs a plan.
 */
export const GOALS: readonly Goal[] = [
  {
    id: "first-win",
    title: "Win your first battle",
    why: "Send your Militia at the Barbarian Outpost — the easy one.",
    action: { kind: "attack", difficulty: "easy" },
    reward: 150,
    stable: true,
    done: (ctx) => breached(ctx, "easy"),
  },
  {
    id: "rebuild-army",
    title: "Rebuild your army",
    why: "Every battle costs units. Train new Militia at the Barracks.",
    action: { kind: "train", unit: "militia", count: 5 },
    // Done as soon as ANY Militia are in training (or the garrison is full
    // again). It used to need ten on the way: train five with three left
    // and the step stayed, attack and lose them and it was further away than
    // before — a step you could not finish (playtest, 29 Sep 2026).
    reward: 120,
    stable: false,
    done: (ctx) =>
      militiaOnTheWay(ctx) >= 10 || ctx.economy.training.some((t) => t.unit === "militia"),
  },
  {
    id: "feed-army",
    title: "Feed the army",
    why: "Every unit eats grain every hour. A bigger farm keeps a bigger army.",
    action: { kind: "build", building: "grain_farm" },
    reward: 200,
    stable: true,
    done: (ctx) => reached(ctx, "grain_farm", 2),
  },
  {
    id: "main-3",
    title: "Main Building to level 3",
    why: "It unlocks the Wall, and everything builds a little faster.",
    action: { kind: "build", building: "main_building" },
    reward: 200,
    stable: true,
    done: (ctx) => reached(ctx, "main_building", 3),
  },
  {
    id: "wall-1",
    title: "Build the Wall",
    why: "Two more road tiles: a longer maze keeps attackers under fire for longer.",
    action: { kind: "build", building: "wall" },
    reward: 150,
    stable: true,
    done: (ctx) => reached(ctx, "wall", 1),
  },
  {
    id: "longer-maze",
    title: "Use your new road",
    why: "Open your defence and make the road longer. Bends are good.",
    action: { kind: "defence" },
    reward: 300,
    stable: false,
    done: (ctx) => roadUsed(ctx.layout) > defenceBudget(0).roadTiles,
  },
  {
    id: "main-5",
    title: "Main Building to level 5",
    why: "It unlocks the Engineer's Guild — more towers, and better ones.",
    action: { kind: "build", building: "main_building" },
    reward: 400,
    stable: true,
    done: (ctx) => reached(ctx, "main_building", 5),
  },
  {
    id: "guild-3",
    title: "Engineer's Guild to level 3",
    why: "A fourth tower slot at level 2, and the armour-piercing Ballista at 3.",
    action: { kind: "build", building: "engineers_guild" },
    reward: 400,
    stable: true,
    done: (ctx) => reached(ctx, "engineers_guild", 3),
  },
  {
    id: "second-win",
    title: "Break the Barbarian Camp",
    why: "The medium one. It holds ten Militia — bring more, or try two waves.",
    action: { kind: "attack", difficulty: "medium" },
    reward: 500,
    stable: true,
    done: (ctx) => breached(ctx, "medium"),
  },
];

/**
 * The step to show: the first one not done, and not already ticked off.
 *
 * `ticked` is what the player has already completed on this device. Steps are
 * sticky once done — the army shrinking after the next battle must not send a
 * player back to "Rebuild your army" in the middle of building the Wall.
 */
export function currentGoal(
  ctx: GoalContext,
  ticked: ReadonlySet<string>
): { goal: Goal | null; index: number; newlyDone: string[] } {
  const newlyDone: string[] = [];
  for (let i = 0; i < GOALS.length; i++) {
    const goal = GOALS[i]!;
    if (ticked.has(goal.id)) continue;
    if (goal.done(ctx)) {
      newlyDone.push(goal.id);
      continue;
    }
    return { goal, index: i, newlyDone };
  }
  return { goal: null, index: GOALS.length, newlyDone };
}

/** A step's reward as resources. */
export function goalReward(goal: Goal): Resources {
  const out = emptyResources();
  for (const kind of RESOURCE_KINDS) out[kind] = goal.reward;
  return out;
}

/**
 * May this step's reward be paid now? Done now — or, for a step that can come
 * undone, any later step done now (see `stable`).
 */
export function goalClaimable(id: string, ctx: GoalContext): boolean {
  const i = GOALS.findIndex((g) => g.id === id);
  if (i < 0) return false;
  const goal = GOALS[i]!;
  if (goal.done(ctx)) return true;
  return !goal.stable && GOALS.slice(i + 1).some((later) => later.done(ctx));
}
